import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { cn } from "@/utils/cn";
import {
  IconCheck,
  IconCurrencyEuro,
  IconRefresh,
  IconSnowflake,
  IconSquareArrowUpFilled,
  IconVaccineBottle,
  IconX,
} from "@tabler/icons-react";
import { NotebookIcon, Package } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTourTaskItem } from "../hooks/useTourTaskItem";
import { InternalTourTask } from "../types";
import { formatPrice } from "../utils/shared";

interface StopItemProps {
  item: InternalTourTask;
  children?: ReactNode;
  onClick: () => void;
}

export function TourTaskItem({ item, children, onClick }: StopItemProps) {
  const { t } = useTranslation();

  const { Icon, text } = useTourTaskItem(item);

  const isDone = item.state === "done";
  const isCanceled = item.state === "canceled";

  const isDisabled = isDone || isCanceled;

  return (
    <div className="relative bg-white p-4 rounded-lg border border-neutral">
      {item.bulkInfo && item.type === "order-delivery" && (
        <Drawer>
          <DrawerTrigger asChild>
            <Badge
              variant="outline"
              className="float-right hover:cursor-pointer"
            >
              Sammelauftrag ({item.bulkTasks?.length} Aufträge)
            </Badge>
          </DrawerTrigger>

          <DrawerContent className="h-1/2">
            <div className="p-4">
              <h3 className="text-lg font-semibold mb-2">
                {item.bulkTasks?.length} Aufträge
              </h3>

              <ul className="bg-slate-50 border-neutral border">
                {item.bulkTasks?.map((task) => (
                  <li key={task.id} className="p-4">
                    {task.name}
                  </li>
                ))}
              </ul>
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {item.isUrgent && (
        <div className="absolute top-4 right-4 bg-[#6A80A8] text-white text-xs font-semibold px-2 py-1 rounded">
          <span className="flex items-center">
            <IconSquareArrowUpFilled className="w-4 h-4 mr-2" />
            {t("tourStopCard.priority")}
          </span>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <div className="flex items-center text-md font-medium text-slate-800 mb-2">
          <Package className="w-4 h-4 mr-2" />
          {item.name}
        </div>

        {item.note && (
          <div className="flex items-center text-gray-500">
            <NotebookIcon className="w-4 h-4 mr-2" />
            {item.note}
          </div>
        )}

        {item.price > 0 && (
          <div className="flex items-center text-gray-500">
            <IconCurrencyEuro className="w-4 h-4 mr-2" />
            {formatPrice(item.price)} €
          </div>
        )}

        {item.isBTM && (
          <div className="flex items-center text-gray-500">
            <IconVaccineBottle className="w-4 h-4 mr-2" />
            {t("tourStopCard.btm")}
          </div>
        )}

        {!item.cooling ||
          (item.cooling !== "none" && (
            <div className="flex items-center text-gray-500">
              <IconSnowflake className="w-4 h-4 mr-2" />
              {t(`tourStopCard.cooling.${item.cooling}`)}
            </div>
          ))}

        {item.jobNr && (
          <div className="flex gap-3 items-center text-gray-500">
            <span>Job-Nr:</span>
            {item.jobNr}
          </div>
        )}

        {children}
      </div>

      <div className="flex justify-between items-center mt-6 gap-2">
        <Button
          className={cn("w-full  rounded-full", {
            "bg-green-100 border-green-400 border-2 text-green-700":
              item.state === "done",
            "bg-red-100 border-danger text-red-700 border-2":
              item.state === "canceled",
          })}
          disabled={isDisabled}
          onClick={onClick}
        >
          {item.state === "done" ? (
            <div className="flex justify-center items-center">
              <IconCheck strokeWidth={2} className="size-6! text-success!" />
            </div>
          ) : item.state === "canceled" ? (
            <div className="flex justify-center items-center">
              <IconX strokeWidth={2} className="size-6! text-danger!" />
            </div>
          ) : (
            <>
              {Icon && <Icon />}
              <span>{text}</span>
            </>
          )}
        </Button>

        {isCanceled && (
          <Button
            className="rounded-full w-[3rem] h-[3rem]"
            variant="outline"
            onClick={onClick}
          >
            <IconRefresh className="text-content" stroke={2.5} />
          </Button>
        )}
      </div>
    </div>
  );
}
